/**
 * Shared MUI Dialog PaperProps for dark layout.
 * Use with <Dialog PaperProps={darkDialogPaperProps}> so dialogs match the app's dark theme.
 */
import { appColors } from './colors';

export const darkDialogPaperProps = {
  sx: {
    backgroundColor: appColors.dialogBg,
    color: 'white',
    '& .MuiDialogTitle-root': {
      color: 'white',
      borderBottom: `1px solid ${appColors.dialogBorder}`,
    },
    '& .MuiDialogContent-root': {
      color: 'white',
      paddingTop: '20px',
    },
    '& .MuiDialogTitle-root + .MuiDialogContent-root': {
      paddingTop: '20px',
    },
    '& .MuiDialogActions-root': {
      borderTop: `1px solid ${appColors.dialogBorder}`,
    },
    '& .MuiTextField-root': {
      '& .MuiOutlinedInput-root': {
        backgroundColor: appColors.dialogBorder,
        color: 'white',
        '& fieldset': {
          borderColor: appColors.dialogBorder,
        },
        '&:hover fieldset': {
          borderColor: appColors.primary,
        },
        '&.Mui-focused fieldset': {
          borderColor: appColors.primary,
        },
      },
      '& .MuiInputLabel-root': {
        color: appColors.mutedText,
        '&.Mui-focused': {
          color: appColors.primary,
        },
      },
    },
    '& .MuiFormControl-root': {
      '& .MuiOutlinedInput-root': {
        backgroundColor: appColors.dialogBorder,
        color: 'white',
        '& fieldset': {
          borderColor: appColors.dialogBorder,
        },
        '&:hover fieldset': {
          borderColor: appColors.primary,
        },
        '&.Mui-focused fieldset': {
          borderColor: appColors.primary,
        },
      },
      '& .MuiInputLabel-root': {
        color: appColors.mutedText,
        '&.Mui-focused': {
          color: appColors.primary,
        },
      },
    },
    '& .MuiButton-root': {
      color: 'white',
      '&.MuiButton-outlined': {
        borderColor: appColors.dialogBorder,
        color: appColors.mutedText,
        '&:hover': {
          borderColor: appColors.primary,
          color: appColors.primary,
          backgroundColor: `${appColors.primary}14`,
        },
      },
      '&.MuiButton-contained': {
        backgroundColor: appColors.primary,
        color: 'white',
        '&:hover': {
          backgroundColor: appColors.primaryDark,
        },
        '&.Mui-disabled': {
          backgroundColor: appColors.disabledBg,
          color: appColors.disabledText,
        },
      },
    },
    '& .MuiAccordion-root': {
      backgroundColor: 'transparent',
      color: 'white',
      '&:before': { display: 'none' },
      '& .MuiAccordionSummary-root': {
        color: 'white',
      },
      '& .MuiAccordionDetails-root': {
        color: 'white',
      },
    },
    '& .MuiTable-root': {
      color: 'white',
    },
    '& .MuiTableCell-root': {
      borderColor: appColors.dialogBorder,
      color: 'white',
    },
    '& .MuiTableHead .MuiTableCell-root': {
      color: appColors.mutedText,
    },
    '& .MuiTypography-root': {
      color: 'inherit',
    },
  },
} as const;

/**
 * Dialog PaperProps matching the Projects page styling (elevation-dark, border-dark).
 * Use for Create/Edit Project dialog so /projects/new has the same CSS look as /projects.
 */
export const projectsDialogPaperProps = {
  sx: {
    backgroundColor: appColors.elevationDark,
    color: 'white',
    '& .MuiDialogTitle-root': {
      color: 'white',
      borderBottom: `1px solid ${appColors.borderDark}`,
    },
    '& .MuiDialogContent-root': {
      color: 'white',
      paddingTop: '20px',
    },
    '& .MuiDialogTitle-root + .MuiDialogContent-root': {
      paddingTop: '20px',
    },
    '& .MuiDialogActions-root': {
      borderTop: `1px solid ${appColors.borderDark}`,
    },
    '& .MuiTextField-root': {
      '& .MuiOutlinedInput-root': {
        backgroundColor: appColors.borderDark,
        color: 'white',
        '& fieldset': {
          borderColor: appColors.borderDark,
        },
        '&:hover fieldset': {
          borderColor: appColors.primary,
        },
        '&.Mui-focused fieldset': {
          borderColor: appColors.primary,
        },
      },
      '& .MuiInputLabel-root': {
        color: appColors.mutedText,
        '&.Mui-focused': {
          color: appColors.primary,
        },
      },
    },
    '& .MuiFormControl-root': {
      '& .MuiOutlinedInput-root': {
        backgroundColor: appColors.borderDark,
        color: 'white',
        '& fieldset': {
          borderColor: appColors.borderDark,
        },
        '&:hover fieldset': {
          borderColor: appColors.primary,
        },
        '&.Mui-focused fieldset': {
          borderColor: appColors.primary,
        },
      },
      '& .MuiInputLabel-root': {
        color: appColors.mutedText,
        '&.Mui-focused': {
          color: appColors.primary,
        },
      },
    },
    '& .MuiButton-root': {
      color: 'white',
      '&.MuiButton-outlined': {
        borderColor: appColors.borderDark,
        color: appColors.mutedText,
        '&:hover': {
          borderColor: appColors.primary,
          color: appColors.primary,
          backgroundColor: `${appColors.primary}14`,
        },
      },
      '&.MuiButton-contained': {
        backgroundColor: appColors.primary,
        color: 'white',
        '&:hover': {
          backgroundColor: appColors.primaryDark,
        },
        '&.Mui-disabled': {
          backgroundColor: appColors.disabledBg,
          color: appColors.disabledText,
        },
      },
    },
    '& .MuiAccordion-root': {
      backgroundColor: 'transparent',
      color: 'white',
      '&:before': { display: 'none' },
      '& .MuiAccordionSummary-root': {
        color: 'white',
      },
      '& .MuiAccordionDetails-root': {
        color: 'white',
      },
    },
    '& .MuiTable-root': {
      color: 'white',
    },
    '& .MuiTableCell-root': {
      borderColor: appColors.borderDark,
      color: 'white',
    },
    '& .MuiTableHead .MuiTableCell-root': {
      color: appColors.mutedText,
    },
    '& .MuiTypography-root': {
      color: 'inherit',
    },
  },
} as const;

/**
 * Same surface as Projects list CRUD dialogs (elevation-dark / border-dark), plus Tabs and Autocomplete
 * so user/role dialogs match without duplicating PaperProps in each tab.
 */
export const listPageDialogPaperProps = {
  sx: {
    ...projectsDialogPaperProps.sx,
    '& .MuiTabs-root': {
      '& .MuiTab-root': {
        color: appColors.mutedText,
        '&.Mui-selected': {
          color: appColors.primary,
        },
      },
      '& .MuiTabs-indicator': {
        backgroundColor: appColors.primary,
      },
    },
    '& .MuiAutocomplete-root': {
      '& .MuiOutlinedInput-root': {
        backgroundColor: appColors.borderDark,
        color: 'white',
        '& fieldset': {
          borderColor: appColors.borderDark,
        },
        '&:hover fieldset': {
          borderColor: appColors.primary,
        },
        '&.Mui-focused fieldset': {
          borderColor: appColors.primary,
        },
      },
      '& .MuiInputLabel-root': {
        color: appColors.mutedText,
        '&.Mui-focused': {
          color: appColors.primary,
        },
      },
    },
    '& .MuiFormHelperText-root': {
      color: appColors.dialogHintText,
      marginTop: '4px',
    },
    '& .MuiFormHelperText-root.Mui-error': {
      color: appColors.formHelperTextError,
    },
    '& .MuiChip-root': {
      backgroundColor: appColors.borderDark,
      color: '#e2e8f0',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      '& .MuiChip-label': {
        color: '#e2e8f0',
      },
      '& .MuiChip-deleteIcon': {
        color: appColors.mutedText,
        '&:hover': {
          color: '#f1f5f9',
        },
      },
    },
  },
} as const;
